export type ImageInputError =
  | 'unsupported'
  | 'too_large'
  | 'invalid_header'
  | 'too_many_pixels'

export interface ImageDimensions {
  width: number
  height: number
}

export type ImageValidationResult =
  | { error: null; dimensions: ImageDimensions }
  | { error: ImageInputError; dimensions: null }

const MAX_FILE_SIZE = 50 * 1024 * 1024
// A 16 MP RGBA preview canvas occupies about 61 MiB by itself. This is the
// largest workload covered by the real Chromium decode/draw/readback gate;
// the former 36 MP limit needed about 137 MiB for the canvas alone.
export const MAX_PIXELS = 16_000_000
const MAX_HEADER_BYTES = 1024 * 1024
const MAX_CONTAINER_CHUNKS = 4_096
const MAX_ANIMATION_FRAMES = 120
// Animated formats can retain multiple decoded frame buffers. Keep their
// aggregate worst-case workload to four maximum-size preview bitmaps.
const MAX_ANIMATION_TOTAL_PIXELS = MAX_PIXELS * 4
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const JPEG_SOF_MARKERS = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf])

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length))
}

function uint16be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0)
}

function uint16le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8)
}

function uint24le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8) | ((bytes[offset + 2] ?? 0) << 16)
}

function uint32be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) * 0x1000000)
    + ((bytes[offset + 1] ?? 0) << 16)
    + ((bytes[offset + 2] ?? 0) << 8)
    + (bytes[offset + 3] ?? 0)
}

function uint32le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0)
    + ((bytes[offset + 1] ?? 0) << 8)
    + ((bytes[offset + 2] ?? 0) << 16)
    + ((bytes[offset + 3] ?? 0) * 0x1000000)
}

export function hasAcceptedImageNameOrType(file: Pick<File, 'name' | 'type'>): boolean {
  if (ACCEPTED_TYPES.includes(file.type.toLowerCase())) return true
  const name = file.name.toLowerCase()
  return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp')
}

function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
}

function isPng(bytes: Uint8Array): boolean {
  return bytes.length >= 24
    && bytes[0] === 0x89
    && bytes[1] === 0x50
    && bytes[2] === 0x4e
    && bytes[3] === 0x47
    && bytes[4] === 0x0d
    && bytes[5] === 0x0a
    && bytes[6] === 0x1a
    && bytes[7] === 0x0a
    && ascii(bytes, 12, 4) === 'IHDR'
}

function isWebp(bytes: Uint8Array): boolean {
  return bytes.length >= 30 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP'
}

function parsePngDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (!isPng(bytes)) return null
  return { width: uint32be(bytes, 16), height: uint32be(bytes, 20) }
}

function parseJpegDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (!isJpeg(bytes)) return null
  let offset = 2

  while (offset < bytes.length) {
    while (offset < bytes.length && bytes[offset] !== 0xff) offset += 1
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1
    if (offset >= bytes.length) return null
    const marker = bytes[offset] ?? 0
    offset += 1

    if (marker === 0xd9 || marker === 0xda) return null
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue
    if (offset + 2 > bytes.length) return null

    const segmentLength = uint16be(bytes, offset)
    if (segmentLength < 2 || offset + segmentLength > bytes.length) return null
    if (JPEG_SOF_MARKERS.has(marker)) {
      if (segmentLength < 7) return null
      return {
        width: uint16be(bytes, offset + 5),
        height: uint16be(bytes, offset + 3),
      }
    }
    offset += segmentLength
  }

  return null
}

function parseWebpDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (!isWebp(bytes)) return null
  const chunk = ascii(bytes, 12, 4)

  if (chunk === 'VP8X') {
    return {
      width: uint24le(bytes, 24) + 1,
      height: uint24le(bytes, 27) + 1,
    }
  }
  if (chunk === 'VP8L' && bytes[20] === 0x2f) {
    const b21 = bytes[21] ?? 0
    const b22 = bytes[22] ?? 0
    const b23 = bytes[23] ?? 0
    const b24 = bytes[24] ?? 0
    return {
      width: 1 + b21 + ((b22 & 0x3f) << 8),
      height: 1 + (b22 >> 6) + (b23 << 2) + ((b24 & 0x0f) << 10),
    }
  }
  if (
    chunk === 'VP8 '
    && bytes[23] === 0x9d
    && bytes[24] === 0x01
    && bytes[25] === 0x2a
  ) {
    return {
      width: uint16le(bytes, 26) & 0x3fff,
      height: uint16le(bytes, 28) & 0x3fff,
    }
  }

  return null
}

export function parseImageDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (isPng(bytes)) return parsePngDimensions(bytes)
  if (isJpeg(bytes)) return parseJpegDimensions(bytes)
  if (isWebp(bytes)) return parseWebpDimensions(bytes)
  return null
}

export function validatePixelDimensions(dimensions: ImageDimensions): ImageInputError | null {
  if (
    !Number.isSafeInteger(dimensions.width)
    || !Number.isSafeInteger(dimensions.height)
    || dimensions.width < 1
    || dimensions.height < 1
  ) {
    return 'invalid_header'
  }
  if (dimensions.width * dimensions.height > MAX_PIXELS) return 'too_many_pixels'
  return null
}

export function validateImageFileMetadata(file: Pick<File, 'name' | 'type' | 'size'>): ImageInputError | null {
  if (!hasAcceptedImageNameOrType(file)) return 'unsupported'
  if (file.size > MAX_FILE_SIZE) return 'too_large'
  return null
}

function addAnimationFrameWorkload(
  totalPixels: number,
  width: number,
  height: number,
  left: number,
  top: number,
  canvas: ImageDimensions,
): number | null {
  if (
    !Number.isSafeInteger(width)
    || !Number.isSafeInteger(height)
    || !Number.isSafeInteger(left)
    || !Number.isSafeInteger(top)
    || width < 1
    || height < 1
    || left > canvas.width - width
    || top > canvas.height - height
  ) return null

  const framePixels = width * height
  if (!Number.isSafeInteger(framePixels) || framePixels > MAX_ANIMATION_TOTAL_PIXELS - totalPixels) return null
  return totalPixels + framePixels
}

function hasValidPngContainer(bytes: Uint8Array, dimensions: ImageDimensions): boolean {
  if (!isPng(bytes)) return false
  let offset = 8
  let chunks = 0
  let declaredFrames: number | null = null
  let frameControls = 0
  let totalFramePixels = 0
  let ended = false

  while (offset < bytes.length) {
    if (++chunks > MAX_CONTAINER_CHUNKS || offset + 12 > bytes.length) return false
    const length = uint32be(bytes, offset)
    const type = ascii(bytes, offset + 4, 4)
    const next = offset + 12 + length
    if (!Number.isSafeInteger(next) || next > bytes.length) return false
    if (type === 'acTL') {
      if (declaredFrames !== null || length !== 8) return false
      declaredFrames = uint32be(bytes, offset + 8)
      if (declaredFrames < 1 || declaredFrames > MAX_ANIMATION_FRAMES) return false
    } else if (type === 'fcTL') {
      frameControls += 1
      if (declaredFrames === null || length !== 26 || frameControls > MAX_ANIMATION_FRAMES) return false
      const nextTotal = addAnimationFrameWorkload(
        totalFramePixels,
        uint32be(bytes, offset + 12),
        uint32be(bytes, offset + 16),
        uint32be(bytes, offset + 20),
        uint32be(bytes, offset + 24),
        dimensions,
      )
      if (nextTotal === null) return false
      totalFramePixels = nextTotal
    } else if (type === 'IEND') {
      if (length !== 0 || next !== bytes.length) return false
      ended = true
      break
    }
    offset = next
  }

  return ended
    && (declaredFrames === null || (
      frameControls === declaredFrames
      && totalFramePixels <= MAX_ANIMATION_TOTAL_PIXELS
    ))
}

function hasValidWebpContainer(bytes: Uint8Array, dimensions: ImageDimensions): boolean {
  if (!isWebp(bytes)) return false
  const riffSize = uint32le(bytes, 4)
  if (riffSize + 8 !== bytes.length) return false
  let offset = 12
  let chunks = 0
  let animationFlag = false
  let animationHeader = false
  let frames = 0
  let totalFramePixels = 0
  let staticImage: ImageDimensions | null = null
  let topLevelPrimaryImages = 0

  const parseBitstreamDimensions = (type: string, dataOffset: number, length: number): ImageDimensions | null => {
    if (type === 'VP8L') {
      if (length < 5 || bytes[dataOffset] !== 0x2f) return null
      const b1 = bytes[dataOffset + 1] ?? 0
      const b2 = bytes[dataOffset + 2] ?? 0
      const b3 = bytes[dataOffset + 3] ?? 0
      const b4 = bytes[dataOffset + 4] ?? 0
      return {
        width: 1 + b1 + ((b2 & 0x3f) << 8),
        height: 1 + (b2 >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10),
      }
    }
    if (
      type === 'VP8 '
      && length >= 10
      && bytes[dataOffset + 3] === 0x9d
      && bytes[dataOffset + 4] === 0x01
      && bytes[dataOffset + 5] === 0x2a
    ) {
      return {
        width: uint16le(bytes, dataOffset + 6) & 0x3fff,
        height: uint16le(bytes, dataOffset + 8) & 0x3fff,
      }
    }
    return null
  }

  const hasMatchingFrameBitstream = (dataOffset: number, length: number, frame: ImageDimensions): boolean => {
    const end = dataOffset + length
    let nestedOffset = dataOffset + 16
    let image: ImageDimensions | null = null
    while (nestedOffset < end) {
      if (++chunks > MAX_CONTAINER_CHUNKS || nestedOffset + 8 > end) return false
      const nestedType = ascii(bytes, nestedOffset, 4)
      const nestedLength = uint32le(bytes, nestedOffset + 4)
      const nestedPaddedLength = nestedLength + (nestedLength % 2)
      const nestedNext = nestedOffset + 8 + nestedPaddedLength
      if (!Number.isSafeInteger(nestedNext) || nestedNext > end) return false
      const nestedDimensions = parseBitstreamDimensions(nestedType, nestedOffset + 8, nestedLength)
      if (nestedDimensions) {
        if (image !== null) return false
        image = nestedDimensions
      }
      nestedOffset = nestedNext
    }
    return nestedOffset === end && image !== null && image.width === frame.width && image.height === frame.height
  }

  while (offset < bytes.length) {
    if (++chunks > MAX_CONTAINER_CHUNKS || offset + 8 > bytes.length) return false
    const type = ascii(bytes, offset, 4)
    const length = uint32le(bytes, offset + 4)
    const paddedLength = length + (length % 2)
    const next = offset + 8 + paddedLength
    if (!Number.isSafeInteger(next) || next > bytes.length) return false
    if (type === 'VP8X') {
      if (length < 10) return false
      animationFlag = ((bytes[offset + 8] ?? 0) & 0x02) !== 0
    } else if (type === 'ANIM') {
      if (length !== 6 || animationHeader) return false
      animationHeader = true
    } else if (type === 'ANMF') {
      if (length < 16 || ++frames > MAX_ANIMATION_FRAMES) return false
      const frame = {
        width: uint24le(bytes, offset + 14) + 1,
        height: uint24le(bytes, offset + 17) + 1,
      }
      const nextTotal = addAnimationFrameWorkload(
        totalFramePixels,
        frame.width,
        frame.height,
        uint24le(bytes, offset + 8) * 2,
        uint24le(bytes, offset + 11) * 2,
        dimensions,
      )
      if (nextTotal === null) return false
      if (!hasMatchingFrameBitstream(offset + 8, length, frame)) return false
      totalFramePixels = nextTotal
    } else if (type === 'VP8 ' || type === 'VP8L') {
      const parsed = parseBitstreamDimensions(type, offset + 8, length)
      // Animated WebP has no top-level primary image bitstream: every frame
      // must be carried by ANMF. Otherwise a tiny animation canvas can hide a
      // huge VP8/VP8L payload from the animation workload checks.
      if (!parsed || ++topLevelPrimaryImages > 1 || animationFlag || animationHeader || frames > 0) return false
      staticImage = parsed
    }
    offset = next
  }

  return offset === bytes.length && (frames === 0
    ? !animationHeader && !animationFlag && staticImage !== null
      && staticImage.width === dimensions.width && staticImage.height === dimensions.height
    : animationFlag && animationHeader && staticImage === null && totalFramePixels <= MAX_ANIMATION_TOTAL_PIXELS)
}

function hasValidAnimatedContainer(bytes: Uint8Array, dimensions: ImageDimensions): boolean {
  if (isPng(bytes)) return hasValidPngContainer(bytes, dimensions)
  if (isWebp(bytes)) return hasValidWebpContainer(bytes, dimensions)
  return true
}

export async function validateImageFile(file: File): Promise<ImageValidationResult> {
  const metadataError = validateImageFileMetadata(file)
  if (metadataError) return { error: metadataError, dimensions: null }

  const headerSize = Math.min(file.size, MAX_HEADER_BYTES)
  const bytes = new Uint8Array(await file.slice(0, headerSize).arrayBuffer())
  const dimensions = parseImageDimensions(bytes)
  if (!dimensions) return { error: 'invalid_header', dimensions: null }
  const dimensionError = validatePixelDimensions(dimensions)
  if (dimensionError) return { error: dimensionError, dimensions: null }

  // Animation and RIFF/PNG chunk counts can sit after the fixed-size image
  // header. Scan the whole accepted file (at most 50 MB) before asking the
  // browser decoder to allocate frame buffers.
  if (isPng(bytes) || isWebp(bytes)) {
    const containerBytes = bytes.length === file.size
      ? bytes
      : new Uint8Array(await file.arrayBuffer())
    if (!hasValidAnimatedContainer(containerBytes, dimensions)) return { error: 'invalid_header', dimensions: null }
  }

  return { error: null, dimensions }
}
